import { FC, MouseEventHandler, useEffect, useState } from 'react';

import { useNavigate } from 'react-router';

import { IconChevronUp } from '@tabler/icons-react';
import { useAtomValue } from 'jotai';

import { useBreakpoints, useMediaQuery } from '@cfa/react-components';

import {
  getHeadingElements,
  removeTopElementStickyClass,
  useBreadcrumbs,
  useTableOfContents,
  useTridionLocalizedContent,
} from '@/utils/document-utils';
import { cn } from '@/utils/tailwind';

import DocumentHead from '@/components/document/DocumentHead';

import '@/styles/document.css';

import { BackToTopCircle, DocumentContainer } from '../../common';
import {
  DesktopView,
  FullscreenGame,
  FullscreenLoading,
  MobileView,
  TridionError,
} from '../../tridion-components';

import { useTridion } from './useTridion';

import { SearchDocument } from '@/_generated/api-clients/xp';
import { RoutedFrom } from '@/atoms/document-persisted';
import { useGameFullscreen } from '@/atoms/document-transient';
import { searchAtom } from '@/atoms/search';

interface TridionProps {
  /** Tridion document. */
  /* Need to discuss the difference between a SearchDocument, BrowseDocument, and PlanResourceReference. updatedAt exists on PlanResourceReference when referring to the Resource, however it is more commonly seen for Plans  */
  document: SearchDocument & { updatedAt?: string };
  /** Icon string for fallback SafeImg rendering */
  icon?: string;
  routedFrom: RoutedFrom;
  isPreview?: boolean;
  isTraining?: boolean;
  handleGoBack?: () => void;
}

/** Tridion document. */
const Tridion: FC<TridionProps> = ({
  document,
  icon,
  routedFrom,
  handleGoBack,
  isPreview,
  isTraining = false,
}) => {
  const navigate = useNavigate();
  const { terms } = useAtomValue(searchAtom);
  const searchTerm = terms[0];
  const [isScrolling, setIsScrolling] = useState(false);
  const [isTocItemClicked, setIsTocItemClicked] = useState(false);
  const { url, language, title } = useTridionLocalizedContent(document);
  const { htmlContent, error, iconContent, summary, documentContent } =
    useTridion(url, {
      documentType: document.type,
      documentName: document.name,
      fallbackVideoUrl: document.contentUrl,
      icon,
    });
  const breakpoints = useBreakpoints();
  const isSmAndDown = useMediaQuery(breakpoints.down('md'));
  const isMobileView = isSmAndDown;
  // Check if in native app or mobile viewport
  const isMobile = window.native.isNative || isSmAndDown;

  const { tocItems } = useTableOfContents(isTocItemClicked, htmlContent);

  const goBack = (isSearch = false) => {
    if (isPreview && handleGoBack) {
      handleGoBack();
    } else if (isSearch) {
      navigate(`/search?query=${searchTerm}`);
    } else {
      navigate(-1);
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolling(window.scrollY > 0);
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handleTocItemClick: MouseEventHandler<HTMLAnchorElement> = e => {
    removeTopElementStickyClass();
    setIsTocItemClicked(true);
    setTimeout(() => {
      setIsTocItemClicked(false);
    }, 2000);
    e.preventDefault();
    const href = (e.target as HTMLLinkElement).href;
    const headingElements = getHeadingElements();
    headingElements.forEach(headingElement => {
      const headingId = headingElement.id;
      if (!(headingId && href.includes(headingId))) return;

      const offset = window.native.isNative ? 12 : -40;

      // Check if we're inside a modal (portal) - find the scrollable container
      const scrollableContainer = headingElement.closest('.overflow-y-auto\\!');
      if (scrollableContainer) {
        // Calculate position relative to the scrollable container
        const containerRect = scrollableContainer.getBoundingClientRect();
        const headingRect = headingElement.getBoundingClientRect();
        const scrollTop =
          scrollableContainer.scrollTop +
          (headingRect.top - containerRect.top) +
          offset;

        scrollableContainer.scrollTo({
          top: scrollTop,
          behavior: 'smooth',
        });
      } else {
        // For main page, scroll the window
        window.scrollTo({
          top: headingElement.offsetTop + offset,
          behavior: 'smooth',
        });
      }
    });
  };

  const breadcrumbs = useBreadcrumbs(
    goBack,
    routedFrom === 'search',
    routedFrom === 'link',
  );

  const [isGameFullscreen] = useGameFullscreen();

  if (isGameFullscreen) {
    return <FullscreenGame htmlContent={htmlContent} />;
  }

  if (!htmlContent) {
    return <FullscreenLoading />;
  }

  if (error) {
    return <TridionError url={document.contentUrl} />;
  }

  return (
    <DocumentContainer isTridion={true} fluid>
      <DocumentHead pageTitle={document.name ?? ''} />
      <div
        data-tridion-content
        data-tridion-title={title}
        className="mt-6 flex flex-col items-center"
      >
        <div className={cn('flex', { 'md:pt-8': !isPreview && !isTraining })}>
          {isMobileView || isPreview ? (
            <MobileView
              breadcrumbs={breadcrumbs}
              documentContent={documentContent}
              handleTocItemClick={handleTocItemClick}
              iconContent={iconContent}
              lastModified={document.lastModified ?? document.updatedAt}
              tags={document.tags ?? []}
              title={title ?? ''}
              tocItems={tocItems}
              isPreview={isPreview}
              isTraining={isTraining}
              language={language}
              type={document.type}
            />
          ) : (
            <DesktopView
              breadcrumbs={breadcrumbs}
              documentContent={documentContent}
              handleTocItemClick={handleTocItemClick}
              iconContent={iconContent}
              lastModified={document.lastModified ?? document.updatedAt}
              tags={document.tags ?? []}
              title={title ?? ''}
              tocItems={tocItems}
              isTraining={isTraining}
              language={language}
              summary={summary}
              type={document.type}
            />
          )}
        </div>
      </div>
      {isScrolling && isMobile && <BackToTop />}
    </DocumentContainer>
  );
};

const BackToTop: FC = () => (
  <BackToTopCircle>
    <IconChevronUp
      className="text-brand-navy!"
      onClick={() => {
        window.scrollTo({
          top: 0,
          behavior: 'smooth',
        });
      }}
    />
  </BackToTopCircle>
);

export default Tridion;
